// File: src/components/game/AddGameModal.js
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert, Row, Col } from "react-bootstrap";
import axios from "axios";

function AddGameModal({ show, handleClose, onGameAdded, seasonId }) {
    const [formData, setFormData] = useState({
        league: "",
        division: "",
        location: "",
        scheduled_date: "",
        home_team: "",
        away_team: "",
    });

    const [locations, setLocations] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // Fetch dropdown data when the modal opens
    useEffect(() => {
        if (!show) return;

        axios.get("/api/leagues/locations/", {
            withCredentials: true,
        })
            .then((response) => setLocations(response.data))
            .catch((error) => console.error("Error fetching locations:", error));

        // Fetch teams for the season using your endpoint (e.g., /api/teams/<season_id>/teams/)
        axios.get(`/api/teams/${seasonId}/teams/`, {
            withCredentials: true,
        })
            .then((response) => setTeams(response.data))
            .catch((error) => console.error("Error fetching teams:", error));
    }, [show, seasonId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage("");

        axios.post(`/api/games/add-game/${seasonId}/`, formData, {
            withCredentials: true,
        })
            .then((response) => {
                onGameAdded(response.data);
                setFormData({
                    league: "",
                    location: "",
                    scheduled_date: "",
                    home_team: "",
                    away_team: "",
                });
                handleClose();
            })
            .catch((error) => {
                console.error("Error adding game:", error);
                if (error.response && error.response.data) {
                    setErrorMessage(error.response.data.error || "Failed to add game.");
                } else {
                    setErrorMessage("An unexpected error occurred.");
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Game</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="location">
                                <Form.Label>Location</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                >
                                    <option value="">Select a location</option>
                                    {locations.map((location) => (
                                        <option key={location.id} value={location.id}>
                                            {location.name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="scheduled_date">
                                <Form.Label>Scheduled Date</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    name="scheduled_date"
                                    value={formData.scheduled_date}
                                    onChange={handleChange}
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    {/* New fields for Home and Away teams */}
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="home_team">
                                <Form.Label>Home Team</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="home_team"
                                    value={formData.home_team}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select a home team</option>
                                    {teams.map((team) => (
                                        <option key={team.id} value={team.id}>
                                            {team.name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="away_team">
                                <Form.Label>Away Team</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="away_team"
                                    value={formData.away_team}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select an away team</option>
                                    {teams.map((team) => (
                                        <option key={team.id} value={team.id}>
                                            {team.name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className="text-end">
                        <Button variant="secondary" onClick={handleClose} className="me-2">
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : "Add Game"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default AddGameModal;
